import { formatConditionals } from './cptDocumentViewUtils';
import { appendConditionBlock, buildConditionBlock } from './cptFormUtils';

const LOOKUP_IMPLANT_ITEM_ID = 'lookup-implant';
const ACTIVE_CONFIG_ITEM_ID = 'lookup-active-config';
const NORDINSIGHT_SOURCE_TYPE = 'nordinsight';

interface ReportingFormImplantSeed {
    name?: string;
    manufacturer?: string;
    model?: string;
    type?: string;
    mriStatus?: number;
    conditionals?: any;
}

interface ReportingFormActiveConfigValue {
    label?: string;
    values?: string[];
}

export interface ReportingFormActiveConfig {
    schemaVersion?: number;
    system?: {
        id?: string;
        version?: number;
        name?: string;
        manufacturer?: string;
        implantType?: string | null;
        mriCode?: string | null;
        ifuSourceUrl?: string | null;
        lastCheckedAt?: string | null;
    };
    status?: 'conditional' | 'unsafe' | string;
    sourceConfigIds?: string[];
    fieldStrengths?: string[];
    scanRegions?: string[];
    deviceSelection?: ReportingFormActiveConfigValue[];
    conditions?: ReportingFormActiveConfigValue[];
}

export interface ReportingFormSeedSource {
    implant?: ReportingFormImplantSeed | null;
    activeConfig?: ReportingFormActiveConfig | null;
}

const getTodayDateString = (): string => new Date().toISOString().split('T')[0];

const ensureNordInsightSource = (
    baseData: Record<string, any>
): Record<string, any> => {
    const existingSources = Array.isArray(baseData.newSource)
        ? [...baseData.newSource]
        : [];
    const hasNordInsightSource = existingSources.some(
        (source: any) => source?.sourceType === NORDINSIGHT_SOURCE_TYPE
    );

    if (hasNordInsightSource) {
        return {};
    }

    return {
        newSource: [
            ...existingSources,
            {
                sourceType: NORDINSIGHT_SOURCE_TYPE,
                sourceDate: getTodayDateString(),
            },
        ],
    };
};

const getExistingImplants = (
    baseData: Record<string, any>
): Record<string, any>[] =>
    Array.isArray(baseData.implants) ? [...baseData.implants] : [];

const escapeHtml = (value: unknown): string =>
    String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');

const formatSectionList = (
    values?: ReportingFormActiveConfigValue[]
): string => {
    const items = (values || [])
        .filter((entry) => entry?.label && entry.values?.length)
        .map(
            (entry) =>
                `<li><strong>${escapeHtml(entry.label)}:</strong> ${escapeHtml(
                    (entry.values || []).join(', ')
                )}</li>`
        );
    return items.length ? `<ul>${items.join('')}</ul>` : '';
};

const buildDeviceSelectionSummary = (
    activeConfig: ReportingFormActiveConfig
): string =>
    (activeConfig.deviceSelection || [])
        .filter((entry) => entry.values?.length)
        .map((entry) => (entry.values || []).join(', '))
        .join(' / ');

export const buildActiveSystemConditionsHtml = (
    activeConfig: ReportingFormActiveConfig
): string => {
    const system = activeConfig.system || {};
    const statusText =
        activeConfig.status === 'conditional'
            ? 'MR Conditional'
            : activeConfig.status === 'unsafe'
            ? 'MR Unsafe'
            : '';
    const headingSuffix = system.mriCode
        ? ` - ${escapeHtml(system.mriCode)}`
        : '';

    const parts: string[] = [
        `<h5><strong>${escapeHtml(
            system.name || 'Active Implant System'
        )}</strong> (${escapeHtml(
            system.manufacturer || 'Unknown manufacturer'
        )})${headingSuffix}</h5>`,
    ];

    if (statusText) {
        parts.push(`<p><strong>MRI status:</strong> ${statusText}</p>`);
    }
    if (activeConfig.fieldStrengths?.length) {
        parts.push(
            `<p><strong>Field strength:</strong> ${escapeHtml(
                activeConfig.fieldStrengths.join(', ')
            )}</p>`
        );
    }
    if (activeConfig.scanRegions?.length) {
        parts.push(
            `<p><strong>Scan region:</strong> ${escapeHtml(
                activeConfig.scanRegions.join(', ')
            )}</p>`
        );
    }

    const deviceSelectionList = formatSectionList(activeConfig.deviceSelection);
    if (deviceSelectionList) {
        parts.push(
            '<h5><strong>Device selection</strong></h5>',
            deviceSelectionList
        );
    }

    const conditionsList = formatSectionList(activeConfig.conditions);
    if (conditionsList) {
        parts.push('<h5><strong>Scan conditions</strong></h5>', conditionsList);
    }


    return `<div id="implant-conditions-${ACTIVE_CONFIG_ITEM_ID}-${escapeHtml(
        system.id || 'unknown'
    )}">${parts.join('')}</div>`;
};

const buildImplantSeed = (
    implant: ReportingFormImplantSeed,
    baseData: Record<string, any>
): Record<string, any> => {
    const seed: Record<string, any> = {
        implants: [
            ...getExistingImplants(baseData),
            {
                id: LOOKUP_IMPLANT_ITEM_ID,
                implantName: implant.name,
                manufacturer: implant.manufacturer,
                implantType: implant.type || '',
            },
        ],
        ...ensureNordInsightSource(baseData),
    };

    if (implant.conditionals) {
        seed.mrConditions = appendConditionBlock(
            baseData.mrConditions,
            buildConditionBlock(
                LOOKUP_IMPLANT_ITEM_ID,
                implant.model,
                implant.manufacturer,
                implant.mriStatus,
                formatConditionals(implant.conditionals)
            )
        );
    }

    return seed;
};

export interface ActiveImplantRef {
    systemId: string;
    systemName?: string;
    configIds: string[];
    version?: number;
    ifuSourceUrl?: string;
}

// Persisted inside the document data (Mixed — unknown keys survive save/load)
// so a saved case can link back to the active implant configuration it was
// created from. Invisible to schema validation by design.
export const ACTIVE_IMPLANT_REF_KEY = '_activeImplantRef';

const buildActiveImplantRef = (
    activeConfig: ReportingFormActiveConfig
): ActiveImplantRef | null => {
    if (!activeConfig.system?.id) return null;
    return {
        systemId: activeConfig.system.id,
        systemName: activeConfig.system.name,
        configIds: activeConfig.sourceConfigIds || [],
        version: activeConfig.system.version,
        ...(activeConfig.system.ifuSourceUrl
            ? { ifuSourceUrl: activeConfig.system.ifuSourceUrl }
            : {}),
    };
};

export const buildActiveImplantBacklinkPath = (
    ref: ActiveImplantRef,
    reportId?: string
): string => {
    const params = new URLSearchParams();
    if (ref.configIds.length) {
        params.set('configs', ref.configIds.join(','));
    }
    // Lets the picker offer a way back to the report it was opened from.
    if (reportId) {
        params.set('report', reportId);
    }
    const query = params.toString();
    return `/active-implants/${ref.systemId}${query ? `?${query}` : ''}`;
};

const buildActiveConfigSeed = (
    activeConfig: ReportingFormActiveConfig,
    baseData: Record<string, any>
): Record<string, any> => {
    const systemName = activeConfig.system?.name || 'Active Implant System';
    const selectionSummary = buildDeviceSelectionSummary(activeConfig);
    const implantLabel = selectionSummary
        ? `${systemName} (${selectionSummary})`
        : systemName;
    const activeImplantRef = buildActiveImplantRef(activeConfig);

    return {
        implants: [
            ...getExistingImplants(baseData),
            {
                id: ACTIVE_CONFIG_ITEM_ID,
                implantName: implantLabel,
                manufacturer: activeConfig.system?.manufacturer,
                implantType: (activeConfig.system?.implantType || '').trim(),
            },
        ],
        ...ensureNordInsightSource(baseData),
        mrConditions: appendConditionBlock(
            baseData.mrConditions,
            buildActiveSystemConditionsHtml(activeConfig)
        ),
        ...(activeImplantRef
            ? { [ACTIVE_IMPLANT_REF_KEY]: activeImplantRef }
            : {}),
    };
};

export const buildDevicePreparationDefaultsFromReportingForm = (
    baseData: Record<string, any>,
    formData?: ReportingFormSeedSource | null
): Record<string, any> => {
    const seededData = {
        ...baseData,
    };

    const activeConfig = formData?.activeConfig;
    if (activeConfig) {
        // The active-implant ingest lands in the 76014 Initial Evaluation
        // only; the 76018 Device Details fields stay empty until the user
        // presses "Copy implant data from Initial Evaluation". Only the
        // backlink reference is carried over.
        const activeImplantRef = buildActiveImplantRef(activeConfig);

        return {
            ...seededData,
            ...(activeImplantRef
                ? { [ACTIVE_IMPLANT_REF_KEY]: activeImplantRef }
                : {}),
        };
    }

    const implant = formData?.implant;
    if (implant) {
        return {
            ...seededData,
            deviceManufacturer: implant.manufacturer || '',
            deviceModelName: implant.model || implant.name || '',
            ...(implant.conditionals
                ? {
                      implantConditions: appendConditionBlock(
                          seededData.implantConditions,
                          buildConditionBlock(
                              LOOKUP_IMPLANT_ITEM_ID,
                              implant.model,
                              implant.manufacturer,
                              implant.mriStatus,
                              formatConditionals(implant.conditionals)
                          )
                      ),
                  }
                : {}),
        };
    }

    return seededData;
};

export const buildInitialEvaluationDefaultsFromReportingForm = (
    baseData: Record<string, any>,
    formData?: ReportingFormSeedSource | null
): Record<string, any> => {
    const seededData = {
        ...baseData,
    };

    if (formData?.implant) {
        Object.assign(
            seededData,
            buildImplantSeed(formData.implant, seededData)
        );
    }

    if (formData?.activeConfig) {
        Object.assign(
            seededData,
            buildActiveConfigSeed(formData.activeConfig, seededData)
        );
    }

    return seededData;
};
